// src/components/doctor-dashboard/UploadPanel.jsx
import React, { useEffect, useRef, useMemo } from "react";
import { Upload, FileText, Plus } from "lucide-react";
import { LabeledInput, LabeledTextarea } from "./ui/DashboardUI";

/**
 * Props:
 * - uploadForm: { title, description, files, isUploading }
 * - onUploadTitleChange, onUploadDescriptionChange, onUploadFilesChange, onSubmitUpload
 * - exposeOpenPicker?: (fn: () => void) => void
 * - title?: string
 */
const UploadPanel = ({
  uploadForm,
  onUploadTitleChange,
  onUploadDescriptionChange,
  onUploadFilesChange,
  onSubmitUpload,
  exposeOpenPicker,
  title = "Share Research",
}) => {
  const inputRef = useRef(null);

  const canSubmit = useMemo(
    () =>
      !uploadForm.isUploading &&
      !!uploadForm.title?.trim() &&
      !!uploadForm.description?.trim() &&
      uploadForm.files &&
      uploadForm.files.length > 0,
    [uploadForm]
  );

  useEffect(() => {
    if (typeof exposeOpenPicker === "function") {
      exposeOpenPicker(() => inputRef.current?.click());
    }
  }, [exposeOpenPicker]);

  const onKeyActivate = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      inputRef.current?.click();
    }
  };

  return (
    <div style={styles.wrap}>
      {/* Local CSS only affects this block */}
      <style>{css}</style>

      {/* Inline “section header” that feels native to the page */}
      <div style={styles.inlineHeader}>
        <Upload style={{ width: 18, height: 18, color: "#0a66c2" }} aria-hidden />
        <h2 style={styles.h2}>{title}</h2>
      </div>

      <div style={styles.grid}>
        <LabeledInput
          label="Title"
          placeholder="Enter research title"
          value={uploadForm.title}
          onChange={onUploadTitleChange}
        />

        <LabeledTextarea
          label="Description"
          placeholder="Describe your research"
          rows={3}
          value={uploadForm.description}
          onChange={onUploadDescriptionChange}
        />

        {/* Files */}
        <div>
          <label style={styles.label}>Files</label>

          {/* Click / drop zone (flat, no card) */}
          <div
            role="button"
            tabIndex={0}
            aria-label="Upload files"
            onKeyDown={onKeyActivate}
            onClick={() => inputRef.current?.click()}
            className="upload-drop-inline"
          >
            <Upload style={{ width: 26, height: 26, color: "#6b7280", marginBottom: 6 }} />
            <div>
              <span style={styles.dropCta}>Click to upload</span>
              <span style={styles.dropHint}> or drag and drop</span>
            </div>
            <p style={styles.dropSub}>
              PNG, JPG, PDF, MP4, WebM (max 3 files, videos up to 15s)
            </p>

            <input
              ref={inputRef}
              id="file-upload"
              type="file"
              accept="image/*,.pdf,video/mp4,video/webm"
              multiple
              onChange={onUploadFilesChange}
              style={{ display: "none" }}
            />
          </div>

          {/* Selected files (flat pills) */}
          {!!uploadForm.files?.length && (
            <div style={{ marginTop: 10 }}>
              <p style={styles.selectedLabel}>
                Selected {uploadForm.files.length} / 3
              </p>
              <div style={styles.pillsRow}>
                {Array.from(uploadForm.files).map((file, i) => (
                  <div key={`${file.name}-${i}`} style={styles.pill} title={file.name}>
                    <FileText style={{ width: 16, height: 16, color: "#0a66c2" }} />
                    <span style={styles.pillText}>{file.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Submit */}
        <div>
          <button
            onClick={onSubmitUpload}
            disabled={!canSubmit}
            style={{
              ...styles.primaryBtn,
              opacity: canSubmit ? 1 : 0.55,
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {uploadForm.isUploading ? (
              <>
                <span style={styles.spinner} />
                Uploading…
              </>
            ) : (
              <>
                <Plus style={{ width: 16, height: 16, marginRight: 8 }} />
                Upload Content
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  wrap: {
    padding: "8px 0 0 0", // no card padding; blend into page
  },
  inlineHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "0 0 10px 0",
    borderBottom: "1px solid #e5e7eb",
    marginBottom: 14,
  },
  h2: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: "#1f2328",
  },
  grid: {
    display: "grid",
    gap: 14,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 8,
  },
  dropCta: { color: "#0a66c2", fontWeight: 700 },
  dropHint: { color: "#6b7280" },
  dropSub: { fontSize: 12, color: "#9ca3af", marginTop: 4, marginBottom: 0 },
  selectedLabel: { fontSize: 13, color: "#6b7280", marginBottom: 6, fontWeight: 600 },
  pillsRow: { display: "grid", gap: 8 },
  pill: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #dbeafe",
    background: "#f3f8ff",
    color: "#1f2328",
    borderRadius: 8,
    padding: "8px 10px",
    minHeight: 34,
    overflow: "hidden",
  },
  pillText: {
    fontSize: 13,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  primaryBtn: {
    width: "100%",
    background: "linear-gradient(135deg, #3b82f6, #6366f1)",
    color: "#fff",
    padding: 14,
    borderRadius: 10,
    fontWeight: 700,
    border: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    boxShadow: "0 12px 20px -10px rgba(59,130,246,0.35)",
    transition: "transform .12s ease, filter .12s ease, box-shadow .12s ease",
  },
  spinner: {
    width: 16,
    height: 16,
    border: "2px solid transparent",
    borderTop: "2px solid #fff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginRight: 8,
  },
};

const css = `
  @keyframes spin { to { transform: rotate(360deg); } }

  /* flat, unobtrusive drop zone (no card look) */
  .upload-drop-inline {
    border: 1px dashed #d1d5db;
    border-radius: 10px;
    padding: 18px;
    text-align: center;
    background: #fafafa;
    color: #374151;
    outline: none;
    transition: box-shadow .15s ease, background .15s ease, border-color .15s ease;
  }
  .upload-drop-inline:hover {
    background: #f7f7f7;
    border-color: #cbd5e1;
  }
  .upload-drop-inline:focus-visible {
    box-shadow: 0 0 0 3px rgba(10, 102, 194, 0.28);
    background: #f7fbff;
    border-color: #93c5fd;
  }

  /* Inputs from DashboardUI should look flat here as well */
  .section-input, .section-textarea {
    border-radius: 8px !important;
    border: 1px solid #e0e0e0 !important;
    background: #fff !important;
  }

  /* Primary button micro-interactions */
  button[disabled] { pointer-events: none; }
  button:not([disabled]):hover { filter: brightness(1.03); transform: translateY(-1px); }
  button:not([disabled]):active { transform: translateY(0); }
`;

export default React.memo(UploadPanel);
